<script setup lang="ts">
import StylessButton from '@/components/StylessButton.vue';
import SearchTemplate from '@/components/Search/SearchTemplate.vue';
import { SearchExtraContent } from '@/models/search-extra-content.model';
import { GroupResults, SearchResult } from '@/models/search.model';
import CmsDataProvider, { CMS_DATA_ID } from "@/providers/cms.provider";
import kenmerkRepository, { Activity, Kenmerk } from '@/repositories/kenmerk.repository';
import { LocationData } from '@/repositories/places.repository';
import { LngLat } from 'mapbox-gl';
import { computed, onBeforeMount, reactive, watch } from 'vue';
import OrderByDropdown, { SortByOption } from '../ActivitySearch/components/OrderByDropdown.vue';
import { useProfileStore } from '@/stores/profile.store';
import { usePlatformStore } from '@/stores/platform.store';
import { useFilters } from '@/helpers/filter.helper';
import { getCurrentLocation } from '@/helpers/location.helper';
import { notify } from '@kyvg/vue3-notification';
import { generateFacetList } from '@/helpers/facet.helper';
import { useDebounceFn } from '@vueuse/core';
import { addToDataLayer } from '@/services/gtm.service';
import LocationSearch from '@/components/Search/LocationSearch.vue';
import DistanceFilter from '../ActivitySearch/components/DistanceFilter.vue';
import FacetFilter from '@/components/ActivitySearchFilters/FacetFilter.vue';
import ActiveFilters from '../ActivitySearch/components/ActiveFilters.vue';
import { HiddenElements, HiddenFilters } from '@/view-models/filters.model';
import SearchSwapper from '@/components/Search/SearchSwapper.vue';
import ClubSearchResult from '@/components/ClubSearchResult.vue';
import sportaanbiederRepository, { ISportaanbiederSearchParams, SportaanbiederEntity } from '@/repositories/sportaanbieder.repository';
import ActivitySearchBox from '../ActivitySearch/components/ActivitySearchBox.vue';
import RatingFilter from '@/components/ActivitySearchFilters/RatingFilter.vue';

const cmsData = CmsDataProvider.get<{
    title?: string,
    searchSubtext?: string,

    clubSearchUrl: string,
    activiteitenSearchUrl: string,
    trainerSearchUrl: string,

    activitiesSingular: string,
    activitiesPlural: string,
    clubsSingular: string,
    clubsPlural: string,
    trainerSingular: string,
    trainerPlural: string,
    hiddenFilters: HiddenFilters,
    hiddenElements: HiddenElements,
    extraContent: SearchExtraContent[]
}>(CMS_DATA_ID.CLUB_SEARCH);

const platformStore = usePlatformStore();
const profileStore = useProfileStore();

const sortOptions: SortByOption[] = [
    { label: "Club (a-z)", value: "Naam" },
    { label: "Club (z-a)", value: "Naam", isDesc: true },
    { label: "Waardering (hoog-laag)", value: "ReviewScore", isDesc: true },
    { label: "Waardering (laag-hoog)", value: "ReviewScore" },
];

const currentSort = computed(() => {
    const foundSortOption = sortOptions.find(option =>
        option.value === searchFilter.filters.OrderBy && option.isDesc == searchFilter.filters.Desc);

    return foundSortOption ?? sortOptions[0];
});

const searchFilter = useFilters<ISportaanbiederSearchParams>({
    type: 'sportaanbieder',
    platform_facet: !platformStore.isGlobalPlatform ? platformStore.platformId : undefined,
    facetlist: generateFacetList(cmsData.hiddenFilters, platformStore.isGolfPlatform),
    pageSize: 20,
    offset: 0,
    OrderBy: 'Naam',
    groupBy: "Id"
}, () => {
    debouncedSearch();
});

const initialSearchResults: SearchResult<SportaanbiederEntity, SportaanbiederEntity> = {
    Count: 0,
    Items: [],
    Query: null,
    Facets: [],
    GroupCount: null,
    Groups: []
};

const state = reactive<{
    isLoading: boolean,
    searchResults: SearchResult<SportaanbiederEntity, SportaanbiederEntity>,
    currentPage: number,
    location?: LngLat,
    selectedDistance?: { label: string, value: number },
    kenmerken: { facet: string, kenmerken: Kenmerk[] }[],
    allKenmerken: Kenmerk[];
    lastFacetChanged?: string,
    selectedLocation?: LocationData,
}>({
    isLoading: true,
    searchResults: initialSearchResults,
    currentPage: 0,
    kenmerken: [],
    allKenmerken: [],
});


const tryDistanceSearch = () => {
    if (state.selectedDistance?.value && state.location?.lat && state.location?.lng) {
        debouncedSearch();
    }
}

const toggleDistanceSortOption = (lat?: number, lng?: number) => {
    if (lat && lng) {
        const sortOptionIndex = sortOptions.findIndex(option => option.value === "Distance,Naam");
        if (sortOptionIndex === -1) {
            sortOptions.push({ label: "Afstand", value: "Distance,Naam" });
            if (!state.selectedDistance) state.selectedDistance = { label: '25 KM', value: 25 };
            searchFilter.setFilter("OrderBy", "Distance,Naam");
        }
    } else {
        const index = sortOptions.findIndex(option => option.value === "Distance,Naam");
        if (index > -1) sortOptions.splice(index, 1);
    }
}

const locateUser = async () => {
    await getCurrentLocation()
        .then(position => {
            state.location = new LngLat(position.coords.longitude, position.coords.latitude)
            toggleDistanceSortOption(position.coords.latitude, position.coords.longitude);
        })
        .catch(error => {
            if (error.code === 1) {
                notify({
                    type: 'warn',
                    title: 'Geen locatie toestemming',
                    text: 'Zonder toestemming is het niet mogelijk om op locatie te zoeken',
                    duration: -1
                })
            }
            else {
                notify({
                    type: 'error',
                    title: 'Er is een fout opgetreden bij het ophalen jouw locatie',
                    duration: 20000
                })
            }
        });
}

const onShowMore = () => {
    debouncedSearch(true);
}

const onSortChange = (sortBy: SortByOption) => {
    searchFilter.setFilter("OrderBy", sortBy.value);
    searchFilter.setFilter("Desc", sortBy.isDesc);
}

const onQueryChange = () => {
    searchFilter.setFilter("query", searchFilter.filters.query);
}

const onFacetChange = (facet: string, selectedFacets?: string[] | number[], activatieFacets?: string[] | number[]) => {
    if (!selectedFacets) {
        state.lastFacetChanged = undefined;
    } else {
        state.lastFacetChanged = selectedFacets.length > 0 ? facet : undefined;
    }

    searchFilter.setFilter(facet as keyof ISportaanbiederSearchParams, selectedFacets ?? activatieFacets);
}

const onAgeChange = () => {
    searchFilter.setFilter("minLeeftijd", searchFilter.filters.minLeeftijd);
}

const onRatingChange = (rating?: number) => {
    if (!rating || rating <= 0) {
        searchFilter.setFilter("fromReviewScore", undefined);
        searchFilter.setFilter("toReviewScore", undefined);
    } else {
        searchFilter.setFilter("fromReviewScore", rating);
        searchFilter.setFilter("toReviewScore", 5);
    }
}

const onDistanceChange = (distance?: { label: string, value: number }) => {
    state.selectedDistance = distance;
    if (distance?.value && state.location) {
        searchFilter.setFilter("distanceInKms", distance.value);
    }
    tryDistanceSearch();
}

const onActivitySelect = (item?: Activity) => {
    if (!item) return;

    const facetKey: keyof ISportaanbiederSearchParams = item.Activiteittype === 1 // Sport type id
        ? "sporten_facet"
        : "cultuuractiviteiten_facet";

    if (searchFilter.filters[facetKey] === undefined) searchFilter.filters[facetKey] = [];

    const index = searchFilter.filters[facetKey]!.indexOf(item.Naam);
    if (index > -1) {
        searchFilter.filters[facetKey]!.splice(index, 1);
    } else {
        searchFilter.filters[facetKey]!.push(item.Naam);
    }
    searchFilter.setFilter(facetKey, searchFilter.filters[facetKey]);
}


const onLocationSelected = (data: LocationData) => {

    addToDataLayer('search', {
        'search_term': data.Naam ?? ''
    });

    if (!state.selectedDistance) {
        state.selectedDistance = { label: '25 KM', value: 25 }
    }
    state.selectedLocation = data;
    state.location = new LngLat(data.Longitude, data.Latitude);

    searchFilter.setFilter("centerLat", state.location.lat);
    searchFilter.setFilter("centerLon", state.location.lng);
    searchFilter.setFilter("distanceInKms", state.selectedDistance.value);
    searchFilter.setFilter("locationName" as any, data.Naam);

    tryDistanceSearch();
}

const getFacetsOfGroup = (facetName: keyof ISportaanbiederSearchParams) => {
    return state.searchResults.Facets?.find(facet => facet.GroupName === facetName)?.Facets ?? [];
    }

const clearDistance = () => {
    state.selectedDistance = undefined;
    state.selectedLocation = undefined;
    state.location = undefined;
    searchFilter.setFilter("OrderBy", "Naam");
    searchFilter.setFilter("locationName" as any, undefined);
}

const getKenmerken = async () => {
    state.allKenmerken = await kenmerkRepository.getKenmerken();

    // Aangepaste sporten (categorie 2)
    if (!cmsData.hiddenFilters.hideAdeptedSportsFilter) {
        const kenmerken = await kenmerkRepository.getKenmerkenByCategorie(2);
        state.kenmerken.push({ facet: "specifiekaanbod_facet", kenmerken });
    }

    // Faciliteiten (categorie 4)
    if (!cmsData.hiddenFilters.hideFacilitiesFilter) {
        const faciliteiten = await kenmerkRepository.getKenmerkenByCategorie(4, platformStore.platformId);
        state.kenmerken.push({ facet: "faciliteiten_facet", kenmerken: faciliteiten });
    }

    // Aanvullende faciliteiten (categorie 9)
    if (!cmsData.hiddenFilters.hideExtraFacilitiesFilter) {
        const aanvullendeFaciliteiten = await kenmerkRepository.getKenmerkenByCategorie(9);
        state.kenmerken.push({ facet: "aanvullendefaciliteiten_facet", kenmerken: aanvullendeFaciliteiten });
    }

    // Lesvormen (categorie 39)
    if (!cmsData.hiddenFilters.hideTeachingMethodsFilter) {
        const lesvormen = await kenmerkRepository.getKenmerkenByCategorie(39);
        state.kenmerken.push({ facet: "lesvormen_facet", kenmerken: lesvormen });
    }
}

const getKenmerk = (facetName: string) => {
    return state.kenmerken.find(kenmerk => kenmerk.facet === facetName)?.kenmerken;
}

const rebuildFilters = () => {

    const searchParams = new URL(window.location.href).searchParams;
    const searchOnLocation = searchParams.get("searchOnLocation") === "true";
    const allEntries = Array.from(searchParams.entries());
    const locationDistanceFilters: { centerLon?: number, centerLat?: number, distanceInKms?: number } = {};

    for (const entry of allEntries) {
        const key = entry[0] as keyof ISportaanbiederSearchParams;
        const value = entry[1];

        if (!value) continue;

        switch (key) {
            // string[]    
            case "specifiekaanbod_facet":
            case "plaatsen_facet":
            case "sporten_facet":
            case "cultuuractiviteiten_facet":
            case "faciliteiten_facet":
            case "aanvullendefaciliteiten_facet":
            case "activiteittypen_facet":
                if (searchFilter.filters[key] === undefined) {
                    searchFilter.filters[key] = [];
                }
                searchFilter.filters[key]!.push(value);
                break;
            // string    
            case "query":
            case "OrderBy":
                Object.assign(searchFilter.filters, { [key]: value });
                break;
            case "Desc":
                searchFilter.filters.Desc = Boolean(value);
                break;
            case "centerLon":
                locationDistanceFilters.centerLon = Number(value);
                break;
            case "centerLat":
                locationDistanceFilters.centerLat = Number(value);
                break;
            case "distanceInKms":
                locationDistanceFilters.distanceInKms = Number(value);
                break;
            default:
                break;
        }
    }

    if (locationDistanceFilters.centerLat && locationDistanceFilters.centerLon && locationDistanceFilters.distanceInKms) {
        Object.assign(searchFilter.filters, {
            ...locationDistanceFilters
        });
        state.location = new LngLat(locationDistanceFilters.centerLon!, locationDistanceFilters.centerLat!);
        state.selectedDistance = { label: `${locationDistanceFilters.distanceInKms} KM`, value: locationDistanceFilters.distanceInKms! };
        const locationName = searchParams.get('locationName');
        if (locationName) {
            state.selectedLocation = {
                Id: 0,
                Naam: locationName,
                Latitude: locationDistanceFilters.centerLat!,
                Longitude: locationDistanceFilters.centerLon!
            };
        }
    } else if (searchOnLocation) {
        locateUser();
        if (!state.selectedDistance) {
            state.selectedDistance = { label: '50 KM', value: 50 }
        }
    } else if (searchFilter.filters.OrderBy === "Distance,StartDatum") {
        searchFilter.setFilter("OrderBy", sortOptions[0].value);
    }
}

const debouncedSearch = useDebounceFn(async (newPage = false) => {
    state.isLoading = true;

    if (newPage) {
        state.currentPage++;
    } else {
        state.currentPage = 0;
    }

    if (state.selectedDistance?.value && state.location?.lat && state.location?.lng) {
        searchFilter.filters.centerLat = state.location.lat;
        searchFilter.filters.centerLon = state.location.lng;
        searchFilter.filters.distanceInKms = state.selectedDistance.value;
    } else if (currentSort.value.value === "Distance,StartDatum") {
        searchFilter.filters.OrderBy = sortOptions[0].value;
        searchFilter.filters.Desc = sortOptions[0].isDesc;
    }


    searchFilter.filters.offset = state.currentPage * searchFilter.filters.pageSize;

    await sportaanbiederRepository.search(searchFilter.filters)
        .then(searchResults => {
            if (newPage) {
                state.searchResults.Items.push(...searchResults.Items);
                state.searchResults.Groups.push(...searchResults.Groups);
            } else {
                state.searchResults = searchResults;
            }

            return searchResults;
        })
        .catch(() => {
            notify({
                type: 'error',
                title: 'Er is een fout opgetreden bij het ophalen van de resultaten',
                duration: 20000
            });
            if (newPage && state.currentPage > 0) state.currentPage--;
        });

    state.isLoading = false;
}, 500);

watch(() => [searchFilter.filters.centerLat, searchFilter.filters.centerLon], ([newLat, newLon]) => {
    toggleDistanceSortOption(newLat, newLon);
});

onBeforeMount(async () => {
    rebuildFilters();
    await getKenmerken();
    await platformStore.ensurePlatform();
    await profileStore.ensureProfile();
    await debouncedSearch();
})

</script>

<template>
    <SearchTemplate :search-item-name-singular="cmsData.clubsSingular" :search-item-name-plural="cmsData.clubsPlural"
        :search-results="state.searchResults" :is-loading="state.isLoading" @show-more="onShowMore"
        :title="cmsData.title" :search-subtext="cmsData.searchSubtext" :extra-content="cmsData.extraContent"
        more-result-button-text="Laad meer">
        <template #searchHeader v-if="!cmsData.hiddenElements?.hideSearchHeader">
            <div v-if="!cmsData.hiddenFilters.hideSearchBox" class="search-input-wrapper">
                <label for="zoekterm-input" class="sr-only">Zoekterm</label>
                <input id="zoekterm-input" class="form-control" placeholder="Waar ben je naar op zoek?"
                    v-model="searchFilter.filters.query" @change="onQueryChange()" />
                <span class="icon icon-search"></span>
            </div>
        </template>

        <template #filters>
            <ActivitySearchBox v-if="!cmsData.hiddenFilters.hideSearchOnSport" @select="onActivitySelect" />

            <LocationSearch v-if="!cmsData.hiddenFilters.hideSearchOnLocation"
                            :selectedLocation="state.selectedLocation"
                            :hide-location-attempt="cmsData.hiddenElements?.hideLocationAttempt" @select="onLocationSelected"
                            @location-attempt="locateUser" />

            <DistanceFilter v-if="!cmsData.hiddenFilters.hideDistanceFilter" :coordinates="state.location"
                            :current-value="searchFilter.filters.distanceInKms" @change="onDistanceChange"
                            @location-attempt="locateUser" />

            <hr />

            <FacetFilter v-if="!cmsData.hiddenFilters.hideActivityTypeFilter" facetName="activiteittypen_facet"
                         :facets="getFacetsOfGroup('activiteittypen_facet')"
                         :activeFacets="searchFilter.filters.activiteittypen_facet" name="Activiteit Type"
                         buttonName="activiteit typen" :lastFacetChanged="state.lastFacetChanged" @change="onFacetChange" />

            <FacetFilter v-if="!cmsData.hiddenFilters.hideCultureActivityFilter" facetName="cultuuractiviteiten_facet"
                         :facets="getFacetsOfGroup('cultuuractiviteiten_facet')"
                         :activeFacets="searchFilter.filters.cultuuractiviteiten_facet" name="Activiteit"
                         buttonName="activiteiten" :lastFacetChanged="state.lastFacetChanged" @change="onFacetChange" />

            <FacetFilter v-if="!cmsData.hiddenFilters.hideSportFilter" facetName="sporten_facet"
                         :facets="getFacetsOfGroup('sporten_facet')" :activeFacets="searchFilter.filters.sporten_facet"
                         name="Sport" buttonName="sporten" :lastFacetChanged="state.lastFacetChanged" @change="onFacetChange" />

            <FacetFilter v-if="!cmsData.hiddenFilters.hideLocationFilter" facetName="plaatsen_facet"
                         :facets="getFacetsOfGroup('plaatsen_facet')" :activeFacets="searchFilter.filters.plaatsen_facet"
                         name="Plaats" buttonName="plaatsen" :lastFacetChanged="state.lastFacetChanged"
                         @change="onFacetChange" />


            <div v-if="!cmsData.hiddenFilters.hideAgeFilter" class="ic-filter">
                <span class="ic-filter-title h4 gray-color">Leeftijd</span>
                <div class="form-group">
                    <input type="number" min="0" max="110" placeholder="Leeftijd" name="leeftijdSearch"
                           v-model="searchFilter.filters.minLeeftijd" class="form-control" @change="onAgeChange" />
                </div>
            </div>

            <RatingFilter v-if="!cmsData.hiddenFilters.hideReviewFilter"
                          :selected="searchFilter.filters.fromReviewScore" @change="onRatingChange" />

            <FacetFilter v-if="!cmsData.hiddenFilters.hideFacilitiesFilter" facetName="faciliteiten_facet"
                         :facets="getFacetsOfGroup('faciliteiten_facet')" :activeFacets="searchFilter.filters.faciliteiten_facet"
                         name="Faciliteiten" buttonName="faciliteiten" :kenmerken="getKenmerk('faciliteiten_facet')"
                         :lastFacetChanged="state.lastFacetChanged" @change="onFacetChange" />

            <FacetFilter v-if="!cmsData.hiddenFilters.hideExtraFacilitiesFilter"
                         facetName="aanvullendefaciliteiten_facet" :facets="getFacetsOfGroup('aanvullendefaciliteiten_facet')"
                         :activeFacets="searchFilter.filters.aanvullendefaciliteiten_facet" name="Aanvullende faciliteiten"
                         buttonName="aanvullende faciliteiten" :kenmerken="getKenmerk('aanvullendefaciliteiten_facet')"
                         :lastFacetChanged="state.lastFacetChanged" @change="onFacetChange" />

            <FacetFilter v-if="!cmsData.hiddenFilters.hideAdeptedSportsFilter" facetName="specifiekaanbod_facet"
                         :facets="getFacetsOfGroup('specifiekaanbod_facet')"
                         :activeFacets="searchFilter.filters.specifiekaanbod_facet" name="Aangepaste sporten"
                         buttonName="aangepaste sporten" :kenmerken="getKenmerk('specifiekaanbod_facet')"
                         :lastFacetChanged="state.lastFacetChanged" @change="onFacetChange" />

            <FacetFilter v-if="!cmsData.hiddenFilters.hideTeachingMethodsFilter" facetName="lesvormen_facet"
                         :facets="getFacetsOfGroup('lesvormen_facet')" :activeFacets="searchFilter.filters.lesvormen_facet"
                         name="Lesvormen" buttonName="lesvormen" :kenmerken="getKenmerk('lesvormen_facet')"
                         :lastFacetChanged="state.lastFacetChanged" @change="onFacetChange" />

            <FacetFilter v-if="!cmsData.hiddenFilters.hideBadgesFilter" facetName="labels_facet"
                         :facets="getFacetsOfGroup('labels_facet')" :activeFacets="searchFilter.filters.labels_facet"
                         name="Label"
                         :lastFacetChanged="state.lastFacetChanged" @change="onFacetChange" />

        </template>

        <template #resultHeader="{ toggleSidebarFilter }">
            <ActiveFilters :search-filter="searchFilter" :day-parts="[]" :kenmerken="state.kenmerken"
                :selected-location="state.selectedLocation" @clear-distance="clearDistance" @change="debouncedSearch" />

            <div class="ordering">
                <SearchSwapper active="clubs" :activities-link="cmsData.activiteitenSearchUrl"
                    :activities-plural="cmsData.activitiesPlural" :clubs-link="cmsData.clubSearchUrl"
                    :clubs-plural="cmsData.clubsPlural" :trainer-link="cmsData.trainerSearchUrl"
                    :trainer-plural="cmsData.trainerPlural" :center-lat="searchFilter.filters.centerLat"
                    :center-lon="searchFilter.filters.centerLon" :distance-in-kms="searchFilter.filters.distanceInKms"
                    :location-name="state.selectedLocation?.Naam" />


                <OrderByDropdown :sort-options="sortOptions" :currentSort="currentSort" @change="onSortChange" />

                <StylessButton class="d-md-none" @click.prevent="toggleSidebarFilter" data-sidebar-filtering-toggle>
                    <span class="icon-sliders" />
                    Filter
                </StylessButton>
            </div>
        </template>

        <template #resultGroup="slotProps: { groups: GroupResults<SportaanbiederEntity> }">
            <template v-for="field in slotProps.groups.GroupFields" :key="field.Entity.SportaanbiederId">
                <ClubSearchResult :trainer-plural="cmsData.trainerPlural" :trainer-singular="cmsData.trainerSingular"
                    :clubs-singular="cmsData.clubsSingular" :all-kenmerken="state.allKenmerken"
                    :entity="field.Entity" />
            </template>
        </template>
    </SearchTemplate>
</template>